import React, { type FC, useRef, useState, useContext } from 'react';

import { useSmartlingStrings } from '@lucid/hooks';
import chunk from 'lodash/chunk';
import kebabCase from 'lodash/kebabCase';

import AccessibleDescription from '../../accessible-description/accessible-description';
import Box from '../../box/box';
import Container from '../../container/container';
import ControlButton from '../../buttons/control-button';
import IntegrationsBannerRow, { type IntegrationItem } from './integrations-banner-row';
import LayoutContext from '../../context/layout-context';

export interface IntegrationsBannerProps {
  /** Allows the component to get additional styles. */
  className?: string;
  /** The integrations data */
  integrations: IntegrationItem[];
}

const IntegrationsBanner: FC<IntegrationsBannerProps> = ({ className, integrations, ...rest }) => {
  /** Will return true if any of the rows are focused, which will pause the animation */
  const [shouldPause, setShouldPause] = useState(false);
  const { shortCode } = useContext(LayoutContext);
  const withSmartling = useSmartlingStrings({
    keys: [
      'mktg_ct_add_on_pause_animation_label',
      'mktg_ct_add_on_play_animation_label',
      'mktg_integrations_banner_description',
    ],
    shortCode,
  });

  const ROW_LENGTH = Math.ceil(integrations.length / 3);
  const chunkIntegrations = chunk(
    integrations.sort((a, b) => a.id.localeCompare(b.id)),
    ROW_LENGTH,
  );

  const containerRef = useRef<HTMLDivElement>(null);
  const tabEscapeRef = useRef<HTMLDivElement>(null);

  return (
    <>
      <AccessibleDescription isForScreenReaderOnly SRText={withSmartling('mktg_integrations_banner_description')} />
      <Box
        {...rest}
        ref={containerRef}
        as="ul"
        css={theme => ({ overflow: 'hidden', padding: `${theme.space.s64}px 0`, margin: 0 })}
        onBlurCapture={() => setShouldPause(false)}
        onFocusCapture={() => setShouldPause(true)}
        onKeyDownCapture={e => {
          if (e.key === 'Escape') {
            tabEscapeRef.current?.focus();
          }
        }}
        // eslint-disable-next-line @stylistic/jsx-sort-props
        className={className}
      >
        {chunkIntegrations.map((row, rowIndex) => (
          <IntegrationsBannerRow
            key={`integration-row-${kebabCase(row[0].title)}`}
            css={theme =>
              rowIndex !== chunkIntegrations.length - 1 && {
                marginBottom: theme.space.s24,
              }
            }
            direction={rowIndex % 2 === 0 ? 'RTL' : 'LTR'}
            duration={integrations.length * 5}
            row={row}
            willAllowPause={shouldPause}
          />
        ))}
      </Box>
      <Container css={{ display: 'flex', justifyContent: 'flex-end' }} extraPadding={3}>
        <ControlButton
          aria-label={withSmartling(`mktg-ct-add-on-${shouldPause ? 'play' : 'pause'}-animation-label`)}
          iconType={shouldPause ? 'PlayOutline' : 'PauseThin'}
          onClick={() => setShouldPause(!shouldPause)}
        />
      </Container>
      <Box ref={tabEscapeRef} tabIndex={0} />
    </>
  );
};

export { IntegrationsBanner };
export default IntegrationsBanner;
